// Wraps MapLibre: base layers, terrain/3D, raster overlays (reach, region heatmap), routes,
// markers and airspace. Svelte components talk to this instead of to MapLibre directly.

import maplibregl, { type GeoJSONSource, type ImageSource, type Map as MLMap, type MapMouseEvent } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { gridCorners, type GridSpec, type LngLat } from '../lib/geo';
import type { Airspace, Basemap, TerrainSource } from '../sources/types';
import { Route3DLayer, type Route3DPoint } from './route3d';
import mlcontour from 'maplibre-contour';
import { BASE, FONT_BOLD, baseLayers, contourThresholds } from './basestyle';
import { GLYPHS, setupGlyphs } from './glyphs';
import type { AltUnit } from '../lib/units';

const EMPTY: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] };

export interface MapHandlers {
  click(at: LngLat): void;
  longPress(at: LngLat): void;
  routeHover(at: LngLat | null): void;
}

export class MapController {
  readonly map: MLMap;
  private ready: Promise<void>;
  private route3d = new Route3DLayer();
  private markers = new Map<string, maplibregl.Marker[]>();
  private cursor: maplibregl.Marker;

  private dem?: InstanceType<typeof mlcontour.DemSource>;
  private base?: { b: Basemap; alt: AltUnit; contours: boolean };

  constructor(container: HTMLElement, terrain: TerrainSource, h: MapHandlers, view?: { center: LngLat; zoom: number }) {
    setupGlyphs();
    const sources: Record<string, maplibregl.SourceSpecification> = {};
    if (terrain.dem) {
      // Contours are generated from the DEM in maplibre-contour's worker. The raster-dem sources
      // fetch the same URLs directly (the HTTP cache dedupes); routing them through the shared
      // protocol let MapLibre's request cancellation poison the contour cache.
      this.dem = new mlcontour.DemSource({
        url: terrain.dem.tiles[0],
        encoding: terrain.dem.encoding,
        // AWS terrain above z12 is upsampled ~30 m data with stair-step artefacts; MapLibre
        // interpolates more smoothly from z12.
        maxzoom: Math.min(terrain.dem.maxzoom, 12),
        worker: true,
      });
      this.dem.setupMaplibre(maplibregl);
      const dem = {
        type: 'raster-dem' as const,
        tiles: terrain.dem.tiles,
        tileSize: terrain.dem.tileSize,
        encoding: terrain.dem.encoding,
        maxzoom: Math.min(terrain.dem.maxzoom, 12),
      };
      sources.dem = { ...dem, attribution: terrain.attribution };
      sources['dem-shade'] = dem;
    }
    this.map = new maplibregl.Map({
      container,
      style: {
        version: 8,
        glyphs: GLYPHS,
        sources,
        layers: [],
        sky: {
          'sky-color': '#9ec5ea',
          'horizon-color': '#e8eef3',
          'fog-color': '#eef2f5',
          'sky-horizon-blend': 0.6,
          'horizon-fog-blend': 0.7,
          'fog-ground-blend': 0.85,
          'atmosphere-blend': ['interpolate', ['linear'], ['zoom'], 9, 1, 13, 0.4],
        },
      },
      center: view?.center ?? [6.5, 46],
      zoom: view?.zoom ?? 8,
      maxPitch: 80,
      attributionControl: { compact: true },
    });
    // Keep the map's centre in the part not covered by the panel (side panel on wide screens,
    // bottom sheet on phones).
    const pad = () =>
      this.map.setPadding(
        innerWidth >= 900 ? { left: 452, top: 0, right: 0, bottom: 0 } : { left: 0, top: 0, right: 0, bottom: Math.round(innerHeight * 0.3) },
      );
    pad();
    addEventListener('resize', pad);
    this.map.addControl(new maplibregl.NavigationControl({ visualizePitch: true }), 'top-right');
    this.map.addControl(new maplibregl.ScaleControl({ unit: 'metric' }), 'bottom-left');
    this.cursor = new maplibregl.Marker({ element: dot('cursor-dot') });

    // Resolve on style load, not 'load': 'load' waits for base-map tiles, which may never arrive
    // without signal. Overlays and the solver must not depend on them.
    this.ready = new Promise((resolve) => {
      const done = () => {
        if (this.map.getSource('reach')) return;
        this.addLayers();
        resolve();
      };
      this.map.once('style.load', done);
      if (this.map.isStyleLoaded()) done();
    });

    // Tap vs long-press (long-press always sets a takeoff on touch devices; right-click on desktop).
    let pressTimer: ReturnType<typeof setTimeout> | undefined;
    let longFired = false;
    this.map.on('touchstart', (e) => {
      if (e.points.length !== 1) return;
      longFired = false;
      pressTimer = setTimeout(() => {
        longFired = true;
        h.longPress([e.lngLat.lng, e.lngLat.lat]);
      }, 600);
    });
    const clear = () => clearTimeout(pressTimer);
    this.map.on('touchend', clear);
    this.map.on('touchmove', clear);
    this.map.on('dragstart', clear);
    this.map.on('contextmenu', (e) => h.longPress([e.lngLat.lng, e.lngLat.lat]));
    this.map.on('click', (e: MapMouseEvent) => {
      if (longFired) return;
      h.click([e.lngLat.lng, e.lngLat.lat]);
    });
    this.map.on('mousemove', 'route-line', (e) => h.routeHover([e.lngLat.lng, e.lngLat.lat]));
    this.map.on('mouseleave', 'route-line', () => h.routeHover(null));
  }

  whenReady(): Promise<void> {
    return this.ready;
  }

  private addLayers() {
    const m = this.map;
    const blank = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==';
    const coords: [LngLat, LngLat, LngLat, LngLat] = [
      [0, 0.001],
      [0.001, 0.001],
      [0.001, 0],
      [0, 0],
    ];
    for (const id of ['reach', 'region']) {
      m.addSource(id, { type: 'image', url: blank, coordinates: coords });
      m.addLayer({ id, type: 'raster', source: id, paint: { 'raster-opacity': 0.7, 'raster-resampling': 'linear', 'raster-fade-duration': 0 } });
    }
    // Crisp outer boundaries of the reach area and launch zones, drawn above the base-map relief.
    m.addSource('region-outline', { type: 'geojson', data: EMPTY });
    m.addSource('reach-outline', { type: 'geojson', data: EMPTY });
    m.addLayer({
      id: 'reach-outline-halo',
      type: 'line',
      source: 'reach-outline',
      layout: { 'line-join': 'round' },
      paint: { 'line-color': 'rgba(255,255,255,0.7)', 'line-width': 4 },
    });
    m.addLayer({
      id: 'reach-outline',
      type: 'line',
      source: 'reach-outline',
      layout: { 'line-join': 'round' },
      paint: { 'line-color': '#9a3412', 'line-width': 1.6 },
    });
    m.addLayer({
      id: 'region-outline-halo',
      type: 'line',
      source: 'region-outline',
      layout: { 'line-join': 'round' },
      paint: { 'line-color': 'rgba(255,255,255,0.75)', 'line-width': 4 },
    });
    m.addLayer({
      id: 'region-outline',
      type: 'line',
      source: 'region-outline',
      layout: { 'line-join': 'round' },
      paint: { 'line-color': '#7c2d12', 'line-width': 1.6 },
    });
    m.addSource('airspace', { type: 'geojson', data: EMPTY });
    m.addLayer({
      id: 'airspace-fill',
      type: 'fill',
      source: 'airspace',
      paint: { 'fill-color': ['case', ['get', 'conflict'], '#e11d48', '#7c3aed'], 'fill-opacity': ['case', ['get', 'conflict'], 0.25, 0.07] },
    });
    m.addLayer({
      id: 'airspace-line',
      type: 'line',
      source: 'airspace',
      paint: { 'line-color': ['case', ['get', 'conflict'], '#e11d48', '#7c3aed'], 'line-width': ['case', ['get', 'conflict'], 2.5, 1.2] },
    });
    m.addLayer({
      id: 'airspace-label',
      type: 'symbol',
      source: 'airspace',
      minzoom: 8,
      layout: {
        'symbol-placement': 'line',
        'text-field': ['get', 'label'],
        'text-size': 11,
        'text-font': FONT_BOLD,
        'symbol-spacing': 400,
      },
      paint: { 'text-color': '#5b21b6', 'text-halo-color': '#fff', 'text-halo-width': 1.5 },
    });
    m.addSource('area', { type: 'geojson', data: EMPTY });
    m.addLayer({ id: 'area-line', type: 'line', source: 'area', paint: { 'line-color': '#0f172a', 'line-width': 2, 'line-dasharray': [2, 2] } });
    m.addSource('route', { type: 'geojson', data: EMPTY });
    m.addLayer({
      id: 'route-casing',
      type: 'line',
      source: 'route',
      filter: ['==', ['geometry-type'], 'LineString'],
      paint: { 'line-color': '#fff', 'line-width': 7 },
      layout: { 'line-cap': 'round', 'line-join': 'round' },
    });
    m.addLayer({
      id: 'route-line',
      type: 'line',
      source: 'route',
      filter: ['==', ['geometry-type'], 'LineString'],
      paint: { 'line-color': ['case', ['get', 'low'], '#dc2626', '#1d4ed8'], 'line-width': 4 },
      layout: { 'line-cap': 'round', 'line-join': 'round' },
    });
    m.addLayer({
      id: 'route-points',
      type: 'circle',
      source: 'route',
      filter: ['==', ['geometry-type'], 'Point'],
      paint: { 'circle-radius': 4, 'circle-color': '#fff', 'circle-stroke-color': '#1d4ed8', 'circle-stroke-width': 2 },
    });
    m.addLayer(this.route3d);
  }

  /** Switch base map, or rebuild it when the units (contour labels) or contour toggle change. */
  setBasemap(b: Basemap, alt: AltUnit, contours: boolean) {
    this.base = { b, alt, contours };
    this.ready.then(() => {
      if (this.base?.b !== b || this.base.alt !== alt || this.base.contours !== contours) return;
      const m = this.map;
      for (const l of m.getStyle().layers) if (l.id.startsWith(BASE)) m.removeLayer(l.id);
      for (const id of Object.keys(m.getStyle().sources))
        if (id.startsWith(BASE) || id === 'contours' || id === 'contour-labels') m.removeSource(id);
      if (this.dem && contours) {
        const url = this.dem.contourProtocolUrl({
          thresholds: contourThresholds(alt),
          multiplier: alt === 'ft' ? 3.28084 : 1,
          contourLayer: 'contours',
          elevationKey: 'ele',
          levelKey: 'level',
          overzoom: 1,
        });
        m.addSource('contours', { type: 'vector', tiles: [url], maxzoom: 15 });
        m.addSource('contour-labels', { type: 'vector', tiles: [url], maxzoom: 15 });
      }
      const layers = baseLayers(b, alt, { shadeSource: 'dem-shade', contours: contours && !!this.dem });
      for (const [id, src] of Object.entries(layers.sources)) m.addSource(id, src);
      for (const l of layers.below) m.addLayer(l, 'reach');
      for (const l of layers.above) m.addLayer(l, 'reach-outline-halo');
    });
  }

  set3D(on: boolean) {
    this.ready.then(() => {
      if (on && this.map.getSource('dem')) {
        this.map.setTerrain({ source: 'dem', exaggeration: 1 });
        if (this.map.getPitch() < 30) this.map.easeTo({ pitch: 60, duration: 800 });
      } else {
        this.map.setTerrain(null);
        this.map.easeTo({ pitch: 0, duration: 600 });
      }
    });
  }

  /** Show a canvas covering the grid as a raster overlay, with an optional outline. */
  setOverlay(id: 'reach' | 'region', spec: GridSpec | null, canvas: HTMLCanvasElement | null, outline?: GeoJSON.FeatureCollection) {
    this.ready.then(() => {
      const src = this.map.getSource(id) as ImageSource | undefined;
      if (!src) return;
      (this.map.getSource(`${id}-outline`) as GeoJSONSource).setData(spec && canvas && outline ? outline : EMPTY);
      if (!spec || !canvas) {
        this.map.setLayoutProperty(id, 'visibility', 'none');
        return;
      }
      src.updateImage({ url: canvas.toDataURL('image/png'), coordinates: gridCorners(spec) });
      this.map.setLayoutProperty(id, 'visibility', 'visible');
    });
  }

  /** Show an RGBA grid as a raster overlay ('reach' or 'region'). */
  setGridImage(id: 'reach' | 'region', spec: GridSpec | null, rgba: Uint8ClampedArray | null) {
    this.ready.then(() => {
      const src = this.map.getSource(id) as ImageSource | undefined;
      if (!src) return;
      if (!spec || !rgba) {
        this.map.setLayoutProperty(id, 'visibility', 'none');
        return;
      }
      const canvas = document.createElement('canvas');
      canvas.width = spec.nx;
      canvas.height = spec.ny;
      canvas.getContext('2d')!.putImageData(new ImageData(new Uint8ClampedArray(rgba), spec.nx, spec.ny), 0, 0);
      src.updateImage({ url: canvas.toDataURL('image/png'), coordinates: gridCorners(spec) });
      this.map.setLayoutProperty(id, 'visibility', 'visible');
    });
  }

  /** Route line (segments coloured by clearance) and waypoint dots, in 2D and 3D. */
  setRoute(waypoints: LngLat[], samples: Route3DPoint[] | null) {
    this.ready.then(() => {
      const features: GeoJSON.Feature[] = [];
      if (samples && samples.length > 1) {
        let run: Route3DPoint[] = [samples[0]];
        const flush = () => {
          if (run.length > 1)
            features.push({
              type: 'Feature',
              properties: { low: run[run.length - 1].low },
              geometry: { type: 'LineString', coordinates: run.map((p) => [p.lng, p.lat]) },
            });
        };
        for (let i = 1; i < samples.length; i++) {
          run.push(samples[i]);
          if (samples[i].low !== samples[i - 1].low) {
            flush();
            run = [samples[i]];
          }
        }
        flush();
      } else if (waypoints.length > 1) {
        features.push({ type: 'Feature', properties: { low: false }, geometry: { type: 'LineString', coordinates: waypoints } });
      }
      for (const p of waypoints) features.push({ type: 'Feature', properties: {}, geometry: { type: 'Point', coordinates: p } });
      (this.map.getSource('route') as GeoJSONSource).setData({ type: 'FeatureCollection', features });
      this.route3d.setPoints(samples ?? []);
    });
  }

  setArea(area: [LngLat, LngLat] | null) {
    this.ready.then(() => {
      const data: GeoJSON.FeatureCollection = area
        ? {
            type: 'FeatureCollection',
            features: [
              {
                type: 'Feature',
                properties: {},
                geometry: {
                  type: 'LineString',
                  coordinates: [
                    [area[0][0], area[0][1]],
                    [area[1][0], area[0][1]],
                    [area[1][0], area[1][1]],
                    [area[0][0], area[1][1]],
                    [area[0][0], area[0][1]],
                  ],
                },
              },
            ],
          }
        : EMPTY;
      (this.map.getSource('area') as GeoJSONSource).setData(data);
    });
  }

  setAirspace(list: Airspace[], conflicts: Set<string>, label: (a: Airspace) => string) {
    this.ready.then(() => {
      const data: GeoJSON.FeatureCollection = {
        type: 'FeatureCollection',
        features: list.map((a) => ({
          type: 'Feature',
          properties: { id: a.id, label: label(a), conflict: conflicts.has(a.id) },
          geometry: { type: 'Polygon', coordinates: a.polygon },
        })),
      };
      (this.map.getSource('airspace') as GeoJSONSource).setData(data);
    });
  }

  /**
   * Replace a group of markers. `onClick` / `onDrag` receive the marker index. Marker elements are
   * created by `make`.
   */
  setMarkers(
    group: string,
    points: LngLat[],
    make: (i: number) => HTMLElement,
    opts: { draggable?: boolean; onClick?: (i: number) => void; onDrag?: (i: number, at: LngLat) => void } = {},
  ) {
    for (const m of this.markers.get(group) ?? []) m.remove();
    const list = points.map((p, i) => {
      const el = make(i);
      const mk = new maplibregl.Marker({ element: el, draggable: !!opts.draggable }).setLngLat(p).addTo(this.map);
      if (opts.onClick)
        el.addEventListener('click', (e) => {
          e.stopPropagation();
          opts.onClick!(i);
        });
      if (opts.onDrag)
        mk.on('dragend', () => {
          const ll = mk.getLngLat();
          opts.onDrag!(i, [ll.lng, ll.lat]);
        });
      return mk;
    });
    this.markers.set(group, list);
  }

  setCursor(at: LngLat | null) {
    if (at) this.cursor.setLngLat(at).addTo(this.map);
    else this.cursor.remove();
  }

  flyTo(at: LngLat, zoom = 12) {
    this.map.flyTo({ center: at, zoom: Math.max(this.map.getZoom(), zoom) });
  }

  fitPoints(pts: LngLat[]) {
    if (!pts.length) return;
    const b = new maplibregl.LngLatBounds(pts[0], pts[0]);
    for (const p of pts) b.extend(p);
    this.map.fitBounds(b, { padding: 40, maxZoom: 14 });
  }

  bounds(): [LngLat, LngLat] {
    const b = this.map.getBounds();
    return [
      [b.getWest(), b.getSouth()],
      [b.getEast(), b.getNorth()],
    ];
  }
}

export function dot(cls: string, text = ''): HTMLElement {
  const el = document.createElement('div');
  el.className = cls;
  el.textContent = text;
  return el;
}
